import useReveal from "@/hooks/useReveal";
import { CalendarDays, FolderOpen, BrainCircuit } from "lucide-react";

const pillars = [
  {
    icon: CalendarDays,
    title: "Run the clinic",
    description: "Scheduling, calendars, and patient flow managed from one central system. Less switching, fewer gaps, smoother days.",
    id: "scheduling",
    accent: "Operational",
  },
  {
    icon: FolderOpen,
    title: "Keep everything connected",
    description: "Patient records, visit history, invoices, and documents linked together. Every touchpoint stays in one place.",
    id: "records",
    accent: "Organised",
  },
  {
    icon: BrainCircuit,
    title: "Complete notes faster",
    description: "AI-assisted documentation turns consultations into structured clinical notes, reviewed and approved by the clinician before saving.",
    id: "ai-notes",
    accent: "AI-powered",
  },
];

const CorePillarsSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      id="features"
      data-testid="core-pillars-section"
      className="py-20 md:py-32"
      style={{ backgroundColor: "#F3F2EE" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`text-center mb-16 reveal-section ${isVisible ? "visible" : ""}`}>
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-5">
            Core pillars
          </p>
          <h2
            data-testid="core-pillars-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-[#1A1B1A]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Everything clinics need, in one flow
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, i) => (
            <div
              key={pillar.id}
              data-testid={`pillar-card-${pillar.id}`}
              className={`lf-card bg-white rounded-3xl border border-[#1A1B1A]/[0.05] p-8 md:p-12 shadow-sm reveal-section ${isVisible ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#205B5A]/[0.08] flex items-center justify-center mb-8">
                <pillar.icon className="w-5 h-5 text-[#205B5A]" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#205B5A] mb-4 block">{pillar.accent}</span>
              <h3
                className="text-xl md:text-2xl font-medium tracking-tight text-[#1A1B1A] mb-4"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {pillar.title}
              </h3>
              <p className="text-base text-[#4A4B4A] leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CorePillarsSection;
